import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  get,
  isEmpty,
  pull,
  find,
  isString,
  take,
  isArray,
  join,
  sortBy,
  filter,
  head,
} from 'lodash';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid2,
  IconButton,
  Typography,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { downloadPdfDocument, getPdfDocument } from '../Patient/_api';
import { getClinicalPortalSetting } from 'modules/Administration/ClinicalPortal/_api';
import { IFieldForPatient, IGridLayoutParams, IPatientDocument } from '../Patient/_types';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useEntityInfo } from 'utils/hooks/useEntityInfo';
import { useLanguage } from 'utils/hooks/useLanguage';
import { useActions } from 'utils/hooks/useActions';
import useAlerts from 'components/Alerts/useAlerts';
import { joinParams } from 'utils/study';
import { useStorage } from 'utils/hooks/useStorage';
import { apiDataToDownloadFile, printOrDownloadBase64String } from 'utils/hooks/useApp';
import Header from 'components/Header/Header';
import { Papeer } from 'components/Papeer/Papeer';
import { DocumentDetailGrids } from './DocumentDetailGrids';
import { DocumentDetailTimeline } from '../Patient/DocumentDetailTimeline';
import { DocumentDetailLayoutDialog } from '../Patient/DocumentDetailLayoutDialog';
import { hookDownloadFromEldax } from '../Patient/hooks';
import { DATA_FOR_DOCUMENT_DETAIL_FROM_STORAGE } from 'constants/constants';
import { findDocuments, findPatients } from './_api';

const formatDate = 'dd. MM. yyyy';

const sxDisplayPrintNone = { '@media print': { display: 'none' } };

export const ClinicalPortalByPatientDocumentsDetailFhir: React.FC = () => {
  const { t } = useTranslation('ClinicalPortal');
  const { toggleLoader } = useAppGlobals();
  const { currentLocale } = useLanguage();
  const { getItem, putItem } = useStorage();
  const navigate = useNavigate();
  const { addErrorAlert } = useAlerts();
  const { storeClinicalPortalSetting } = useActions();
  const { id } = useEntityInfo();
  const {
    selectedDocumentsForDocumentDetail,
    selectedDocuments,
    clinicalPortalSettingFromRedux,
    fhirPatientDocumentsFromRedux,
    patients,
  } = useAppInfo();

  const linkBack =
    window.location.pathname.replace('portal/', '').replace('/documents', '') + '?action=back';

  const [entity, setEntity] = useState<any>(null);
  const [gridLayout, setGridLayout] = useState<string>('');
  const [patientDocuments, setPatientDocuments] = useState<IPatientDocument[]>([]);
  const [documentUids, setDocumentUids] = useState<any[]>([]);
  const [fieldsForPatientName, setFieldsForPatientName] = useState<IFieldForPatient>();
  const [fieldsForPatientInfo, setFieldsForPatientInfo] = useState<IFieldForPatient[]>([]);
  const [openLayoutDialog, setOpenLayoutDialog] = useState<boolean>(false);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [standardizedPrint, setStandardizedPrint] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(true);
  const [isDownloadingFromEldax, setIsDownloadingFromEldax] = useState<boolean>(false);

  const entityJsonString = JSON.stringify(entity);

  const toggleExpanded = () => setExpanded(!expanded);

  const loadDocuments = async () => {
    toggleLoader(true, false, 'loadDocuments');
    let pomMasterPatientID = get(selectedDocumentsForDocumentDetail, 'masterPatientID', id);
    let pomDocuments = get(selectedDocumentsForDocumentDetail, 'documents', []);
    let pomSelectedDocuments = selectedDocuments;
    if (!isEmpty(pomMasterPatientID) && !isEmpty(pomDocuments)) {
      putItem(
        DATA_FOR_DOCUMENT_DETAIL_FROM_STORAGE,
        JSON.stringify({
          masterPatientID: pomMasterPatientID,
          documents: pomDocuments,
          selectedDocuments: pomSelectedDocuments,
        }),
      );
    } else {
      const dataForDocumentDetailFromStorage = getItem(DATA_FOR_DOCUMENT_DETAIL_FROM_STORAGE);
      if (dataForDocumentDetailFromStorage) {
        // Parse it to a javaScript object
        try {
          const parsedDataForDocumentDetailFromStorage = JSON.parse(
            dataForDocumentDetailFromStorage,
          );
          pomMasterPatientID = get(parsedDataForDocumentDetailFromStorage, 'masterPatientID');
          pomDocuments = get(parsedDataForDocumentDetailFromStorage, 'documents', []);
          pomSelectedDocuments = get(
            parsedDataForDocumentDetailFromStorage,
            'selectedDocuments',
            [],
          );
        } catch (err) {
          console.log(err);
        }
      }
    }

    const selectedDocumentsCount = pomDocuments.length;
    if (selectedDocumentsCount > 0) {
      setGridLayout(
        `${Math.ceil(selectedDocumentsCount / 4)}x${
          selectedDocumentsCount > 4 ? 4 : selectedDocumentsCount
        }`,
      );
    } else {
      setGridLayout('1x1');
    }
    let patient = find(patients, { masterPatientID: id });
    if (!patient) {
      const findPatientArray = await findPatients({ masterPatientID: id.toString() });
      patient = isArray(findPatientArray) && find(findPatientArray, { masterPatientID: id });
    }
    if (!patient) {
      addErrorAlert(t('getDataError'));
      navigate(linkBack);
      return;
    }
    let documents = fhirPatientDocumentsFromRedux;
    if (isEmpty(documents)) {
      let clinicalPortalSetting = clinicalPortalSettingFromRedux;
      if (!clinicalPortalSetting) {
        clinicalPortalSetting = await getClinicalPortalSetting();
        storeClinicalPortalSetting(clinicalPortalSetting);
      }
      let activeTab = '';
      let bookmarks: any[] = [];
      if (clinicalPortalSetting) {
        bookmarks = sortBy(filter(get(clinicalPortalSetting, 'bookmarks', []), { enabled: true }), [
          'position',
        ]);
      }
      const first = head(bookmarks);
      if (first !== undefined) {
        activeTab = get(first, 'id', '');
      }
      const documentArray: IPatientDocument[] = await findDocuments(id, activeTab);
      if (documentArray) {
        documents = documentArray.map((item, index: number) => ({
          ...item,
          index,
          uuidex: `${item.uuid}_${index}`,
          cannotBySelected: get(item, 'typeOfDocument.name', '') === 'DICOM',
          showDocumentIcon: 'Search',
          typeOfDocumentName: get(item, 'typeOfDocument.translateJson')
            ? get(
                JSON.parse(get(item, 'typeOfDocument.translateJson')),
                currentLocale,
                get(item, 'typeOfDocument.name'),
              )
            : '?',
        }));
      }
    }
    if (!documents) {
      addErrorAlert(t('noFindPatient'));
      navigate(linkBack);
    }

    setEntity(patient);

    const preparedDocuments = documents.map((item: any) => ({
      ...item,
      typeOfDocumentName: get(item, 'typeOfDocument.translateJson')
        ? get(
            JSON.parse(get(item, 'typeOfDocument.translateJson')),
            currentLocale,
            get(item, 'typeOfDocument.name'),
          )
        : '?',
      patientEntity: patient,
      patient,
    }));
    setPatientDocuments([...preparedDocuments]);
    setDocumentUids(pomSelectedDocuments);
    toggleLoader(false, false, 'loadDocuments');
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let fieldsForPatientName = {};
    let fieldsForPatientInfo: any[] = [];
    if (entity) {
      fieldsForPatientName = {
        name: 'name',
        value: `${joinParams([
          get(entity, 'prefix'),
          get(entity, 'lastName'),
          get(entity, 'firstName'),
          get(entity, 'middleName'),
          get(entity, 'suffix'),
          `(${t('patientIdentificationNumber')}: ${
            get(entity, 'identificationNumber', '-') != null
              ? get(entity, 'identificationNumber', '-')
              : '-'
          } | ${t('patientBirthDate')}: ${
            get(entity, 'birthDate', '')
              ? format(new Date(get(entity, 'birthDate', '')), formatDate)
              : '-'
          })`,
        ])}
        `,
        lg: 6,
      };
      fieldsForPatientInfo = [
        {
          name: 'patientID',
          label: t('patientInsuranceNumber'),
          value: `${get(entity, 'insuranceNumber', '-') || '-'}`,
        },
        {
          name: 'patientSex',
          label: t('patientSex'),
          value: t(`patient_sex_${get(entity, 'sex', 'O')}`),
        },
        {
          name: 'address',
          label: t('address'),
          value: joinParams(
            [
              get(entity, 'address', ''),
              joinParams([get(entity, 'postcode', ''), get(entity, 'town', '')]),
            ],
            ', ',
          ),
        },
        {
          name: 'telephone',
          label: t('telephone'),
          value: `${
            isArray(get(entity, 'telephone', '')) ? join(get(entity, 'telephone'), ',') : ''
          }`,
        },
        {
          name: 'motherMaidenName',
          label: t('birthLastName'),
          value: `${get(entity, 'birthLastName', '') || ''}`,
        },
        {
          name: 'masterPatientID',
          label: t('masterPatientID'),
          value: `${get(entity, 'masterPatientID', '') || ''}`,
        },
      ];
    }
    setFieldsForPatientName(fieldsForPatientName);
    setFieldsForPatientInfo(fieldsForPatientInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityJsonString]);

  const loadDocumentForPrint = async (
    masterPatientID: any,
    documentUiid: any,
    typeOfDocument: any,
    isPrinting: boolean,
    isExporting: boolean,
    standardizedPrint: boolean,
  ) => {
    if (masterPatientID !== null && documentUiid !== null && typeOfDocument !== null) {
      const documentPrintInfoResponse = await getPdfDocument(
        masterPatientID,
        documentUiid,
        typeOfDocument,
        standardizedPrint,
      );
      if (documentPrintInfoResponse) {
        return documentPrintInfoResponse;
      } else {
        if (isPrinting) {
          addErrorAlert(t('printDocumentError'));
        }
        if (isExporting) {
          addErrorAlert(t('exportDocumentError'));
        }
      }
    }
    return undefined;
  };

  const printOrExport = async () => {
    toggleLoader();
    const uuid = selectedDocuments[0];
    if (uuid) {
      const patientdoc = find(patientDocuments, { uuid });
      const masterPatientID = get(entity, 'masterPatientID', null);
      const fileName = masterPatientID + '.pdf';
      if (isPrinting) {
        const base64PdfString = await loadDocumentForPrint(
          masterPatientID,
          uuid,
          get(patientdoc, 'typeOfDocument.name', null),
          isPrinting,
          isExporting,
          standardizedPrint,
        );
        const contentType = 'application/pdf';
        printOrDownloadBase64String(base64PdfString, contentType, fileName);
        setStandardizedPrint(false);
        setIsPrinting(false);
      } else if (isExporting) {
        const base64PdfString = await downloadPdfDocument(
          masterPatientID,
          uuid,
          get(patientdoc, 'typeOfDocument.name', ''),
          standardizedPrint,
        );
        apiDataToDownloadFile(base64PdfString, fileName);
        setStandardizedPrint(false);
        setIsExporting(false);
      }
    }
    toggleLoader(false);
  };

  useEffect(() => {
    if (isPrinting || isExporting) {
      printOrExport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrinting, isExporting]);

  const downloadFromEldaxFunction = async () => {
    toggleLoader();
    for await (const uuid of documentUids) {
      const patientdoc = find(patientDocuments, { uuid });
      const masterPatientID = get(entity, 'masterPatientID', null);
      hookDownloadFromEldax(
        masterPatientID,
        patientdoc,
        uuid,
        t,
        addErrorAlert,
        setIsDownloadingFromEldax,
      );
    }
    toggleLoader(false);
  };
  useEffect(() => {
    if (isDownloadingFromEldax) {
      downloadFromEldaxFunction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownloadingFromEldax]);

  const unselectDocumentUuid = (uuid: string) => {
    const reducedDocumentUids = [...documentUids];
    pull(reducedDocumentUids, uuid);

    setDocumentUids(reducedDocumentUids);
  };
  const selectDocumentUuid = (uuid: string) => {
    const updatedDocumentUids = [...documentUids];
    updatedDocumentUids.push(uuid);

    setDocumentUids(updatedDocumentUids);
  };

  const replaceLastDocumentUuid = (uuid: string) => {
    const currentUids = [...documentUids];
    currentUids[currentUids.length - 1] = uuid;
    setDocumentUids(currentUids);
  };

  const replaceDocumentAtIndex = (uuid: string, index: number) => {
    const currentUids = [...documentUids];
    currentUids[index] = uuid;
    setDocumentUids(currentUids);
  };

  const [gridLayoutParams, setGridLayoutParams] = useState<IGridLayoutParams>({
    x: 1,
    y: 1,
    totalAvailableFields: 1,
    lastDocumentPositionIndex: 1,
  });

  useEffect(() => {
    if (isString(gridLayout)) {
      const dimensions = gridLayout.split('x');
      if (isArray(dimensions) && dimensions.length === 2) {
        const x = Number(dimensions[0]);
        const y = Number(dimensions[1]);
        const max = x * y;
        const newUids = take(documentUids, max);
        setDocumentUids(newUids);
        setGridLayoutParams({
          x,
          y,
          totalAvailableFields: x * y,
          lastDocumentPositionIndex: documentUids.length - 1,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLayout, documentUids?.length]);

  return (
    <>
      {entity && (
        <>
          <Typography component="div" sx={sxDisplayPrintNone}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Header title={t('documentsDetail')} backUrl={linkBack} />
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={() => setOpenLayoutDialog(true)}
                style={{ marginBottom: '8px', marginInlineStart: 'auto' }}
              >
                <ViewColumnIcon />
              </IconButton>
            </Box>

            <Papeer>
              <Box id="patient" sx={sxDisplayPrintNone}>
                <Accordion elevation={0} defaultExpanded={false} onChange={() => toggleExpanded()}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel-patient"
                    id="panel-patient"
                  >
                    <Typography>{`${get(fieldsForPatientName, 'value', '?')}`}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid2 container={true} spacing={2}>
                      {fieldsForPatientInfo.map((field) => (
                        <Grid2 size={{ xs: 6, md: 4, lg: 3 }} {...field.grid} key={field.name}>
                          <Box component="label" sx={{ fontWeight: 'bold' }}>
                            {field.label}
                          </Box>
                          <div>{field.value}</div>
                        </Grid2>
                      ))}
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Papeer>
          </Typography>

          {!isEmpty(patientDocuments) && gridLayout && (
            <Box sx={sxDisplayPrintNone}>
              <DocumentDetailTimeline
                patientDocuments={patientDocuments}
                gridLayout={gridLayout}
                documentUids={documentUids}
                setDocumentUids={setDocumentUids}
                selectDocumentUuid={selectDocumentUuid}
                unselectDocumentUuid={unselectDocumentUuid}
                replaceLastDocumentUuid={replaceLastDocumentUuid}
                gridLayoutParams={gridLayoutParams}
              />

              <DocumentDetailGrids
                gridLayout={gridLayout}
                documentUids={documentUids}
                selectDocumentUuid={selectDocumentUuid}
                unselectDocumentUuid={unselectDocumentUuid}
                patientDocuments={patientDocuments}
                setIsPrinting={setIsPrinting}
                setStandardizedPrint={setStandardizedPrint}
                setIsExporting={setIsExporting}
                requestedDocuments={[]}
                gridLayoutParams={gridLayoutParams}
                replaceDocumentAtIndex={replaceDocumentAtIndex}
                setIsDownloadingFromEldax={setIsDownloadingFromEldax}
                facilities={[]}
              />
            </Box>
          )}
        </>
      )}

      {openLayoutDialog && (
        <DocumentDetailLayoutDialog
          openLayoutDialog={openLayoutDialog}
          setOpenLayoutDialog={setOpenLayoutDialog}
          setGridLayout={setGridLayout}
          gridLayoutParams={gridLayoutParams}
        />
      )}
    </>
  );
};
